import { Column, Index, JoinColumn, ManyToOne, OneToMany } from 'typeorm';
import { Entity, Column as LabeledColumn } from '../../decorators';
import { BaseEntity } from './base.entity';
import { ClientEntity } from './client.entity';
import { CompanyEntity } from './company.entity';
import { FileEntity } from './file.entity';
import { MPaymentPaymentMethodEntity } from './m-payment-payment-method.entity';
import { MPaymentTransactionSummaryEntity } from './m-payment-transaction-summary.entity';
import { MSmPaymentTransactionInvoiceEntity } from './m-sm-payment-transaction-invoice.entity';
import { MSmPaymentTransactionOrderEntity } from './m-sm-payment-transaction-order.entity';
import { PaymentProcessorConfigEntity } from './payment-processor-config.entity';

@Index('m_payment_fk__file__id_signature', ['mPaymentFkFileIdSignature'], {})
@Index(
	'IDX_m_payment_transaction_fk_payment_processor_config__id',
	['fkPaymentProcessorConfigId'],
	{},
)
@Index(
	'm_payment_fk_m_payment_payment_method__id',
	['mPaymentFkMPaymentPaymentMethodId'],
	{},
)
@Index(
	'm_payment_fk_m_payment_transaction__id_origin',
	['mPaymentFkMPaymentTransactionIdOrigin'],
	{},
)
@Index(
	'm_payment_fk_m_payment_transaction__id_parent',
	['mPaymentFkMPaymentTransactionIdParent'],
	{},
)
@Index('m_sm_payment_fk_client__id', ['mSmPaymentFkClientId'], {})
@Index('m_sm_payment_fk_company__id', ['mSmPaymentFkCompanyId'], {})
@Index(
	'payment_transaction_externalId_erp_fk_company_id_index',
	['mPaymentExternalIdErp', 'mSmPaymentFkCompanyId'],
	{ unique: true },
)
@Index('idx_txn_datetime', ['mPaymentDatetime'], {})
@Index(
	'idx_txn_origin_deleted',
	['mPaymentFkMPaymentTransactionIdOrigin', 'deletedUserId'],
	{},
)
@Entity('m_payment_transaction', {}, { label: 'Payment Transaction' })
export class MPaymentTransactionEntity extends BaseEntity {
	@Column('varchar', {
		name: 'm_payment_externalId_paymentProcessor',
		nullable: true,
		length: 64,
	})
	public mPaymentExternalIdPaymentProcessor!: string | null;

	/**
	 * PD-17281 — the gateway's reusable token for a card that was used once
	 * and NOT saved. A card hold placed at storefront checkout is collected
	 * later by the process payments job, and an invoice above the hold or a
	 * backordered shipment needs a further charge or a new hold on the same
	 * card. The form's own token expires within minutes, so the gateway is
	 * asked for a reusable one when the hold is placed and it is kept here,
	 * on the transaction, instead of in the customer's saved payment methods.
	 * Null for saved-method and non-card transactions.
	 */
	@Column('varchar', {
		name: 'm_payment_externalId_paymentInstrument',
		nullable: true,
		length: 64,
	})
	public mPaymentExternalIdPaymentInstrument!: string | null;

	/**
	 * PD-17393 — the merchant reference code we sent to the payment gateway
	 * with this request (CyberSource's clientReferenceInformation.code, e.g.
	 * `txn_<clientId>_<ms>` for a sale or hold, `capture_…` for a capture,
	 * `refund_…` for a refund). The processor's own id for the transaction is
	 * already kept in m_payment_externalId_paymentProcessor, but finance
	 * searches the gateway's reports by this code, so mover-integration
	 * copies both onto the ERP payment record when the tenant configures it.
	 * Kept on failed attempts too, since the gateway still logged the
	 * request under this code. Null for cash, check, account funds, gateways
	 * that do not take a merchant reference, and payments recorded before
	 * PD-17393.
	 */
	@Column('varchar', {
		name: 'm_payment_externalId_merchantReference',
		nullable: true,
		length: 64,
	})
	public mPaymentExternalIdMerchantReference!: string | null;

	@Column('varchar', {
		name: 'm_payment_externalId_erp',
		nullable: true,
		length: 64,
	})
	public mPaymentExternalIdErp!: string | null;

	/**
	 * PD-15553 — when the ERP-side reversal of this FAILED, already-exported
	 * payment completed (Workday: Unapply_Customer_Payment →
	 * Cancel_Customer_Payment → Cancel_Customer_Invoice_Adjustment for its
	 * processing-fee adjustments). Written by mover-integration's
	 * payment-export reversal sweep, which selects on this being NULL so a
	 * completed reversal is never retried. NULL = no reversal has happened
	 * (payment never failed after export, or the sweep hasn't reached it yet).
	 */
	@LabeledColumn(
		'datetime',
		{
			name: 'm_payment_reversalDatetime_erp',
			nullable: true,
		},
		{ label: 'ERP Reversal Date' },
	)
	public mPaymentReversalDatetimeErp!: Date | null;

	@Column('int', {
		name: 'm_payment_fk_m_payment_payment_method__id',
		nullable: true,
		unsigned: true,
	})
	public mPaymentFkMPaymentPaymentMethodId!: number | null;

	@Column('datetime', { name: 'm_payment_datetime' })
	public mPaymentDatetime!: Date;

	@Column('varchar', { name: 'm_payment_type', nullable: true, length: 64 })
	public mPaymentType!: string | null;

	@Column('varchar', {
		name: 'm_payment_checkNumber',
		nullable: true,
		length: 32,
	})
	public mPaymentCheckNumber!: string | null;

	@Column('text', { name: 'm_payment_description', nullable: true })
	public mPaymentDescription!: string | null;

	@Column('varchar', { name: 'm_payment_origin', nullable: true, length: 64 })
	public mPaymentOrigin!: string | null;

	@Column('varchar', { name: 'm_payment_status', nullable: true, length: 64 })
	public mPaymentStatus!: string | null;

	@Column('decimal', {
		name: 'm_payment_amount',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public mPaymentAmount!: string | null;

	@Column('varchar', {
		name: 'm_payment_resultCode',
		nullable: true,
		length: 64,
	})
	public mPaymentResultCode!: string | null;

	@Column('text', { name: 'm_payment_resultText', nullable: true })
	public mPaymentResultText!: string | null;

	@Column('int', {
		name: 'm_payment_fk_m_payment_transaction__id_parent',
		nullable: true,
		unsigned: true,
	})
	public mPaymentFkMPaymentTransactionIdParent!: number | null;

	@Column('int', {
		name: 'm_payment_fk_m_payment_transaction__id_origin',
		nullable: true,
		unsigned: true,
	})
	public mPaymentFkMPaymentTransactionIdOrigin!: number | null;

	@Column('int', {
		name: 'm_payment_fk__file__id_signature',
		nullable: true,
		unsigned: true,
	})
	public mPaymentFkFileIdSignature!: number | null;

	@Column('int', { name: 'm_sm_payment_fk_client__id', unsigned: true })
	public mSmPaymentFkClientId!: number;

	@Column('int', { name: 'm_sm_payment_fk_company__id', unsigned: true })
	public mSmPaymentFkCompanyId!: number;

	/**
	 * PD-16204 — the processor configuration (the merchant account) that
	 * moved this money. A refund or void has to go back through the account
	 * that took the charge, and once a tenant runs an account per company
	 * that is no longer implied by the tenant alone. Stamped by every
	 * gateway-backed write in TransactionService; NULL on rows written before
	 * it existed, on manual instruments (cash, check, account funds), and on
	 * the legacy 1.0 write path — those resolve through the row's company at
	 * read time. Cleared when the configuration is deleted.
	 */
	@Column('int', {
		name: 'fk_payment_processor_config__id',
		nullable: true,
		unsigned: true,
	})
	public fkPaymentProcessorConfigId!: number | null;

	@Column('varchar', {
		name: 'm_payment_payment_method_type',
		length: 64,
		default: () => "'Credit Card'",
	})
	public mPaymentPaymentMethodType!: string;

	@Column('varchar', {
		name: 'm_payment_payment_method_subtype',
		nullable: true,
		length: 64,
	})
	public mPaymentPaymentMethodSubtype!: string | null;

	@Column('char', {
		name: 'm_payment_payment_method_lastFour',
		nullable: true,
		length: 4,
	})
	public mPaymentPaymentMethodLastFour!: string | null;

	@Column('varchar', {
		name: 'm_payment_payment_method_name',
		nullable: true,
		length: 255,
	})
	public mPaymentPaymentMethodName!: string | null;

	@Column('datetime', {
		name: 'm_payment_externalModifiedDatetime_erp',
		nullable: true,
	})
	public mPaymentExternalModifiedDatetimeErp!: Date | null;

	@Column('varchar', { name: 'm_payment_fee_unit', nullable: true, length: 32 })
	public mPaymentFeeUnit!: string | null;

	/**
	 * The CONFIGURED fee value in force when this transaction ran — `3.00` with
	 * a unit of '%' means three percent, NOT three dollars. It records what the
	 * rate was and labels the row for the customer; it is not a currency amount
	 * and must never be treated as one. Subtracting it directly from a charge is
	 * what recorded a $3.28 authorization as $0.28 net (PD-15077).
	 *
	 * For the fee in dollars, read `mPaymentFeeTotal` below.
	 */
	@Column('decimal', {
		name: 'm_payment_fee_amount',
		precision: 13,
		scale: 2,
		default: () => "'0.00'",
	})
	public mPaymentFeeAmount!: string;

	/**
	 * PD-15143 — the processing fee this transaction carried, in DOLLARS,
	 * computed once when the money moved and stored.
	 *
	 * Signed, so a document's fee is a plain SUM over its transactions with no
	 * per-type branching at the call site:
	 *
	 *   - a capture or sale stores what it took (positive);
	 *   - a refund stores the share it gave back (negative) — the processor
	 *     returns the fee on a refund, so this is symmetric for customer and
	 *     tenant;
	 *   - an authorization stores 0.00 ALWAYS, whatever its status. The capture
	 *     that consumes it carries the fee, and counting both would double it;
	 *   - a failed charge and a void store 0.00.
	 *
	 * Written by `calculateTransactionFee`, which prefers the exact
	 * `gross − goods` over inferring from the rate. Replaces the previous model
	 * where the fee was re-derived on every read from the rate stamp, by two
	 * different formulas that could disagree.
	 */
	@Column('decimal', {
		name: 'm_payment_fee_total',
		precision: 13,
		scale: 2,
		default: () => "'0.00'",
	})
	public mPaymentFeeTotal!: string;

	/**
	 * PD-15186 — the processing fee a refund to ACCOUNT FUNDS kept back, in
	 * dollars. Set only on Refund rows whose money went to the customer's
	 * account instead of the original card: `mPaymentAmount` is what was
	 * credited (the goods share) and `mPaymentFeeTotal` is 0.00, because no fee
	 * came back from the processor. The original charge's fee stays untouched,
	 * so a SUM of recorded fees still equals what the gateway took. Together,
	 * amount + feeWithheld is the portion of the original charge that was
	 * reversed, which is what the refundable cap counts.
	 */
	@Column('decimal', {
		name: 'm_payment_feeWithheld',
		precision: 13,
		scale: 2,
		default: () => "'0.00'",
	})
	public mPaymentFeeWithheld!: string;

	/**
	 * PD-15142 — how the card used for this transaction is funded: `credit`,
	 * `debit`, `prepaid`, `charge`, `deferred_debit`, `unknown` (the gateway
	 * could not classify it) or `unavailable` (the lookup was unreachable). Null
	 * for non-card transactions and for rows written before this was captured.
	 *
	 * Stamped at transaction time from the payment method's resolved category so
	 * the record shows what was known when the surcharge decision was made. Only
	 * `credit` may be surcharged — see `isSurchargeableCardCategory`.
	 */
	@Column('varchar', {
		name: 'm_payment_cardCategory',
		nullable: true,
		length: 32,
	})
	public mPaymentCardCategory!: string | null;

	@Column({
		type: 'boolean',
		name: 'm_payment_isSettled',
		width: 1,
		default: () => "'0'",
	})
	public mPaymentIsSettled!: boolean;

	@ManyToOne(
		() => MPaymentPaymentMethodEntity,
		(mPaymentPaymentMethod) => mPaymentPaymentMethod.mPaymentTransactions,
		{ onDelete: 'NO ACTION', onUpdate: 'NO ACTION' },
	)
	@JoinColumn([
		{
			name: 'm_payment_fk_m_payment_payment_method__id',
			referencedColumnName: 'id',
		},
	])
	public mPaymentFkMPaymentPaymentMethod!: MPaymentPaymentMethodEntity;

	@ManyToOne(
		() => MPaymentTransactionEntity,
		(mPaymentTransaction) => mPaymentTransaction.mPaymentTransactions,
		{ onDelete: 'NO ACTION', onUpdate: 'NO ACTION' },
	)
	@JoinColumn([
		{
			name: 'm_payment_fk_m_payment_transaction__id_parent',
			referencedColumnName: 'id',
		},
	])
	public mPaymentFkMPaymentTransactionIdParent2!: MPaymentTransactionEntity;

	@OneToMany(
		() => MPaymentTransactionEntity,
		(mPaymentTransaction) =>
			mPaymentTransaction.mPaymentFkMPaymentTransactionIdParent2,
		{},
	)
	public mPaymentTransactions!: Array<MPaymentTransactionEntity>;

	@ManyToOne(
		() => MPaymentTransactionEntity,
		(mPaymentTransaction) => mPaymentTransaction.mPaymentTransactions2,
		{ onDelete: 'NO ACTION', onUpdate: 'NO ACTION' },
	)
	@JoinColumn([
		{
			name: 'm_payment_fk_m_payment_transaction__id_origin',
			referencedColumnName: 'id',
		},
	])
	public mPaymentFkMPaymentTransactionIdOrigin2!: MPaymentTransactionEntity;

	@OneToMany(
		() => MPaymentTransactionEntity,
		(mPaymentTransaction) =>
			mPaymentTransaction.mPaymentFkMPaymentTransactionIdOrigin2,
		{},
	)
	public mPaymentTransactions2!: Array<MPaymentTransactionEntity>;

	@ManyToOne(() => FileEntity, (file) => file.mPaymentTransactions, {
		onDelete: 'NO ACTION',
		onUpdate: 'NO ACTION',
	})
	@JoinColumn([
		{ name: 'm_payment_fk__file__id_signature', referencedColumnName: 'id' },
	])
	public mPaymentFkFileIdSignature2!: FileEntity;

	@ManyToOne(() => ClientEntity, (client) => client.transactions, {
		onDelete: 'NO ACTION',
		onUpdate: 'NO ACTION',
	})
	@JoinColumn([
		{ name: 'm_sm_payment_fk_client__id', referencedColumnName: 'id' },
	])
	public mSmPaymentFkClient!: ClientEntity;

	@ManyToOne(() => CompanyEntity, (company) => company.transactions, {
		onDelete: 'NO ACTION',
		onUpdate: 'NO ACTION',
	})
	@JoinColumn([
		{ name: 'm_sm_payment_fk_company__id', referencedColumnName: 'id' },
	])
	public mSmPaymentFkCompany!: CompanyEntity;

	@ManyToOne(
		() => PaymentProcessorConfigEntity,
		(config) => config.transactions,
		{
			onDelete: 'SET NULL',
			onUpdate: 'NO ACTION',
		},
	)
	@JoinColumn([
		{ name: 'fk_payment_processor_config__id', referencedColumnName: 'id' },
	])
	public fkPaymentProcessorConfig!: PaymentProcessorConfigEntity | null;

	@OneToMany(
		() => MPaymentTransactionSummaryEntity,
		(mPaymentTransactionSummary) =>
			mPaymentTransactionSummary.mPaymentFkMPaymentTransactionIdOrigin2,
		{},
	)
	public mPaymentTransactionSummaries!: Array<MPaymentTransactionSummaryEntity>;

	@OneToMany(
		() => MSmPaymentTransactionInvoiceEntity,
		(mSmPaymentTransactionInvoice) =>
			mSmPaymentTransactionInvoice.mSmPaymentFkMPaymentTransaction,
		{},
	)
	public mSmPaymentTransactionInvoices!: Array<MSmPaymentTransactionInvoiceEntity>;

	@OneToMany(
		() => MSmPaymentTransactionOrderEntity,
		(mSmPaymentTransactionOrder) =>
			mSmPaymentTransactionOrder.mSmPaymentFkMPaymentTransaction,
		{},
	)
	public mSmPaymentTransactionOrders!: Array<MSmPaymentTransactionOrderEntity>;
}
